<template>
  <div class="i-first-step">
    <LandingPhoto />

    <div class="content">
      <LandingLogo :visible="vis.logo" />

      <div class="main">
        <LandingHero :visible="vis.hero" />
        <LandingCards
          :visible="vis.cards"
          @pick="onPick"
        />
      </div>

      <div class="bottom">
        <LandingTrust :visible="vis.trust" />
      </div>
    </div>

    <LandingOverlay
      :visible="overlayVisible"
      :message="overlayMsg"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from "vue"
import LandingPhoto from "@/components/firstStep/LandingPhoto.vue"
import LandingLogo from "@/components/firstStep/LandingLogo.vue"
import LandingHero from "@/components/firstStep/LandingHero.vue"
import LandingCards from "@/components/firstStep/LandingCards.vue"
import LandingTrust from "@/components/firstStep/LandingTrust.vue"
import LandingOverlay from "@/components/firstStep/LandingOverlay.vue"

const vis = reactive({ logo: false, hero: false, cards: false, trust: false })
const overlayVisible = ref(false)
const overlayMsg = ref("Building your profile...")

onMounted(() => {
  const schedule = [
    [60, "logo"],
    [210, "hero"],
    [470, "cards"],
    [650, "trust"]
  ]
  schedule.forEach(([delay, key]) => {
    setTimeout(() => {
      vis[key] = true
    }, delay)
  })

  // Lock scroll on desktop (md+), mirroring the original HTML behaviour
  const mq = window.matchMedia("(min-width: 768px)")
  function applyLock(e) {
    document.body.classList.toggle("is-landing-desktop", e.matches)
  }
  applyLock(mq)
  mq.addEventListener("change", applyLock)
  // Store cleanup reference
  vis._mq = mq
  vis._applyLock = applyLock
})

onUnmounted(() => {
  document.body.classList.remove("is-landing-desktop")
  if (vis._mq && vis._applyLock) {
    vis._mq.removeEventListener("change", vis._applyLock)
  }
})

function onPick(card) {
  overlayMsg.value = card.msg
  setTimeout(() => {
    overlayVisible.value = true
  }, 180)
}
</script>

<style lang="scss" scoped>
.i-first-step {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  background: $white;

  .content {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px 20px 24px;
    margin-top: 32vh;
    min-height: calc(100vh - 32vh);

    @include respond-to($breakpoint-sm) {
      padding: 20px 24px 28px;
    }

    @include respond-to($breakpoint-md) {
      position: fixed;
      top: 0;
      right: 0;
      width: 54%;
      height: 100%;
      margin-top: 0;
      padding: 32px 56px 32px 48px;
      min-height: unset;
    }
  }

  .main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;

    @include respond-to($breakpoint-md) {
      gap: 0;
      margin-bottom: 0;
    }
  }

  .bottom {
    border-top: 1px solid #0a0e1a14;
    padding-top: 16px;
    margin-top: 20px;

    @include respond-to($breakpoint-md) {
      margin-top: 0;
    }
  }
}
</style>

<style lang="scss">
// Landing page desktop locks scroll; quiz and mobile scroll freely
body.is-landing-desktop {
  overflow: hidden;
}

.i-first-step {
  font-family: $sans;
  background: $white;
  color: $ink;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;

  ::selection {
    background: $navy-pale;
    color: $amber-mid;
  }

  @keyframes rot {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
